import type { StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import moment from "moment";
import React, { forwardRef, useEffect, useMemo, useState, useRef } from "react";
import { Trans } from "react-i18next";
import type StripeTypes from "stripe";
import styled from "styled-components";

import { PaymentRequiredError } from "apis/_sdk/types/errors";
import { WorkspacePlanTier, WorkspaceUserRole } from "common/interfaces";
import stripeProducts, { type RecurringPlanDefinition } from "common/stripeProducts";
import sanitizeRelativeUrl from "common/utils/sanitizeRelativeUrl";
import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import { isPPTAddin } from "js/config";
import { upgradeWorkspacePlan } from "js/controllers/BillingController";
import { createWorkspace, setPersistentWorkspaceId } from "js/controllers/WorkspaceController";
import Api from "js/core/api";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { preventZoom } from "js/core/utilities/preventZoom";
import { trackActivity } from "js/core/utilities/utilities";
import { BeautifulDialog, ShowDialog, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { useUser } from "js/react/hooks/useUser";
import { useUserWorkspaces } from "js/react/hooks/useWorkspace";
import { useScreenSize } from "js/react/hooks/useScreenSize";

import SuccessfulPurchaseDialog from "../../../dialogs/SuccessfulPurchaseDialog";
import { getProductDetails } from "../../utils/prices";

import { t } from "js/i18n";
import { AddressForm } from "../../components/AddressForm/AddressForm";
import { PaymentForm } from "../../components/PaymentForm/PaymentForm";
import { PlanFeatures } from "../../components/PlanFeatures/PlanFeatures";
import { PlanSummary } from "../../components/PlanSummary/PlanSummary";
import { PromoCoupon, PromoForm } from "../../components/PromoForm/PromoForm";
import { SubmissionButtons } from "../../components/SubmissionButtons/SubmissionButtons";
import { TermsOfUseAndRefundPolicy } from "../../components/TermsOfUseAndRefundPolicy/TermsOfUseAndRefundPolicy";
import { LoadingState } from "../components/LoadingState";
import withElements from "../../../withElements";
import withStripe from "../../../withStripe";
import { ProCheckoutBillingIntervalText } from "./ProCheckoutBillingIntervalText";
import ProgressBar from "js/react/components/ProgressBar";
import { getStaticUrl } from "js/config";

const logger = getLogger(LogGroup.BILLING);

export const ProCheckoutContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow-y: auto;
`;

export const ProCheckoutContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: 5px;
    padding-left: 5px;
    gap: 10px;
    min-height: 0;
`;

export const ProCheckoutButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-shrink: 0;
    background: #fff;
    padding-top: 20px;
    z-index: 1;
`;

export interface ProCheckoutState {
    isTrial: boolean,
    isAnnual: boolean,
    isUpgradeMode: boolean,
    isSubmitting: boolean,
    canSubmit: boolean,
    showProgressLoader: boolean,
    paymentMethodCreationError: string | null,
    billingAddress: StripeTypes.Address,
    billingInterval: "month" | "year",
    productDisplayName: string,
    prices: { month: RecurringPlanDefinition, year: RecurringPlanDefinition },
    promo: StripeTypes.PromotionCode | null,
    availableReferralCredit: number | null,
    handleBillingIntervalChange: (billingInterval: "month" | "year") => void,
    handlePaymentElementChange: (paymentElementChangeEvent: StripePaymentElementChangeEvent) => void,
    handleBillingAddressChange: ({ address, isAddressValid }: { address: Pick<StripeTypes.Address, "country" | "postal_code">, isAddressValid: boolean }) => void,
    handleVerifyPromoCode: (promo: StripeTypes.PromotionCode) => Promise<void>,
    handleSubmitPayment: (stripe: Stripe, elements: StripeElements) => void,
    handleCancel: () => void,
}

interface ProCheckoutProps {
    stripe?: Stripe;
    elements?: StripeElements;
    defaultBillingInterval?: "month" | "year";
    priceIds?: string[];
    displayName?: string;
    workspaceId?: string;
    closeDialog?: () => void;
    onSuccess?: () => void;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    background: #FFF;
    overflow: hidden;
    position: relative;
`;

const StickyHeader = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    padding: 30px 30px 10px;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 5px;
    flex-shrink: 0;
    background: #FFF;
    z-index: 10;
`;

const ScrollContent = styled.div`
    position: absolute;
    top: 120px;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    padding: 0px 30px 150px;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 20px;
`;

const StickyBottom = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    padding: 20px 30px;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;
    background: #F7FAFC;
    box-shadow: 0 -2px 25px 0 rgba(0, 0, 0, 0.08);
    z-index: 10;
`;

const FlowProgressContainer = styled.div`
    width: 100%;
    flex-shrink: 0;
    position: sticky;
    bottom: 0;
    background: #FFF;
    z-index: 10;
`;

const ProgressInfoContainer = styled.div`
    display: flex;
    padding: 20px;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    align-self: stretch;
`;

const ProgressWrapper = styled.div`
    flex: 1 1 auto;
    min-width: 0;
`;

const LogoWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const BeautifulLogo = styled.img`
    width: 100px;
`;

const OverlayContainer = styled.div<{ useMobileLayout?: boolean, useFullScreen?: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 0;
    background: #FFF;
    z-index: 1000;
    box-shadow: none;
    overflow: hidden;

    height: 76vh;
    width: ${({ useMobileLayout }) => useMobileLayout ? "600px" : "825px"};

    border-radius: 4px;
    box-shadow: 0px 9px 37px 0px rgba(0, 0, 0, 0.2);

    ${({ useMobileLayout }) => useMobileLayout && `
        height: 90vh;
        width: 600px;
    `}
    ${({ useFullScreen }) => useFullScreen && `
        height: 100vh;
        height: 100dvh;
        width: 100vw;
    `}
`;

const CheckoutContentContainer = styled.div`
    align-self: stretch;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    padding-right: 0;

    .dialog-content {
        padding: 0 34px;
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;
    }
`;

export const ProCheckoutLayout = withStripe(withElements(({
    isTrial,
    isAnnual,
    isUpgradeMode,
    isSubmitting,
    canSubmit,
    showProgressLoader,
    paymentMethodCreationError,
    billingAddress,
    billingInterval,
    productDisplayName,
    prices,
    promo,
    availableReferralCredit,
    handleBillingIntervalChange,
    handlePaymentElementChange,
    handleBillingAddressChange,
    handleVerifyPromoCode,
    handleSubmitPayment,
    handleCancel,
    stripe,
    elements
}: ProCheckoutState & { stripe: Stripe, elements: StripeElements }) => {
    const { isMobile, width: windowWidth } = useScreenSize();
    const useMobileLayout = isMobile || windowWidth < 916;
    const submitRef = useRef<any>(null);

    if (isSubmitting && showProgressLoader) {
        return (<LoadingState
            title={isTrial ? t("Starting free trial") : t("Upgrading plan")}
            subtitle={t("This will only take a moment...")}
        />);
    }

    const annualCostAtMonthlyRate = prices.month.amount * 12 / 100;
    const annualCostAtAnnualRate = prices.year.amount / 100;
    const annualSavingsPercentage = (1 - annualCostAtAnnualRate / annualCostAtMonthlyRate) * 100;

    const titleText = isTrial ? t("Start your 14 day free trial") : t("Upgrade to Beautiful.ai {{productDisplayName}}.", { productDisplayName });
    const subtitleText = isTrial ? t("Cancel before your trial ends and you won't be charged.") : t("Sorry, you are not eligible for a 14-day free trial because you have already had a trial in the past.");

    const submitLabel = isTrial ? t("Start Free Trial") : isUpgradeMode ? t("Upgrade Now") : t("Confirm");

    if (useMobileLayout) {
        return (
            <Container>
                <FetchingClickShield visible={isSubmitting} backgroundColor="white" />

                <StickyHeader>
                    <TextContent dialogTitle center>{titleText}</TextContent>
                    <TextContent dialogSubtitle center>
                        {subtitleText}
                    </TextContent>
                </StickyHeader>

                <ScrollContent>
                    <PaymentForm
                        variant="B"
                        paymentMethodCreationError={paymentMethodCreationError}
                        handlePaymentElementChange={handlePaymentElementChange}
                    />
                    <AddressForm
                        variant="B"
                        initialAddress={billingAddress}
                        onChange={handleBillingAddressChange}
                    />
                    {!promo ? <PromoForm variant="B" verifyPromoCode={handleVerifyPromoCode} /> : <PromoCoupon variant="B" coupon={promo.coupon} />}
                    <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                        <PlanFeatures useNarrowFormat={true} isAnnual={isAnnual} isTrial={isTrial} />
                    </div>
                </ScrollContent>

                <StickyBottom>
                    <PlanSummary
                        variant="B"
                        expandable
                        defaultExpanded={false}
                        isTrial={isTrial}
                        billingInterval={billingInterval}
                        productDisplayName={productDisplayName}
                        prices={prices}
                        promo={promo}
                        availableReferralCredit={availableReferralCredit}
                        handleBillingIntervalChange={handleBillingIntervalChange}
                        handleVerifyPromoCode={handleVerifyPromoCode}
                    />

                    <SubmissionButtons
                        submitLabel={submitLabel}
                        onSubmit={() => handleSubmitPayment(stripe, elements)}
                        disabled={!canSubmit}
                        onCancel={isUpgradeMode ? handleCancel : undefined}
                    />

                    <TextContent disclaimer center style={{ marginTop: -10, marginBottom: -10 }}>
                        <ProCheckoutBillingIntervalText
                            isAnnual={isAnnual}
                            monthlyPrice={prices.month.amount / 100}
                            annualCostAtMonthlyRate={annualCostAtMonthlyRate}
                            annualSavingsPercentage={annualSavingsPercentage}
                            onSwitchToMonthly={() => handleBillingIntervalChange("month")}
                            onSwitchToAnnual={() => handleBillingIntervalChange("year")}
                            includeTerms
                            termsComponent={<TermsOfUseAndRefundPolicy textDecoration="none" />}
                        />
                    </TextContent>
                </StickyBottom>
            </Container>
        );
    }

    return (
        <FlexLayout justify="center" align="flex-start" width="fill" height="fill" padding="20px 20px 0" gap={20}>
            <ProCheckoutContainer style={{ padding: `15px 30px` }}>
                <FlexLayout direction="column" gap={5}>
                    <TextContent dialogTitle>{titleText}</TextContent>
                    <TextContent dialogSubtitle>
                        {subtitleText}
                    </TextContent>
                </FlexLayout>

                <ProCheckoutContentContainer style={{ gap: 15 }}>
                    <PaymentForm
                        variant="B"
                        paymentMethodCreationError={paymentMethodCreationError}
                        handlePaymentElementChange={handlePaymentElementChange}
                    />
                    <AddressForm
                        variant="B"
                        initialAddress={billingAddress}
                        onChange={handleBillingAddressChange}
                        nextFieldRef={submitRef}
                        elements={elements}
                    />
                    <TextContent disclaimer>
                        {t("By providing your card information, you allow Beautiful.ai to charge your card for future payments in accordance with their terms.")}
                    </TextContent>
                </ProCheckoutContentContainer>

                <ProCheckoutButtonsContainer>
                    <SubmissionButtons
                        ref={submitRef}
                        submitLabel={submitLabel}
                        onSubmit={() => handleSubmitPayment(stripe, elements)}
                        disabled={!canSubmit}
                        onCancel={isUpgradeMode ? handleCancel : undefined}
                    />

                    <TextContent disclaimer center>
                        <TermsOfUseAndRefundPolicy size="small" />
                    </TextContent>
                    <TextContent disclaimer center style={{ marginBottom: -10 }}>
                        <ProCheckoutBillingIntervalText
                            isAnnual={isAnnual}
                            monthlyPrice={prices.month.amount / 100}
                            annualCostAtMonthlyRate={annualCostAtMonthlyRate}
                            annualSavingsPercentage={annualSavingsPercentage}
                            onSwitchToMonthly={() => handleBillingIntervalChange("month")}
                            onSwitchToAnnual={() => handleBillingIntervalChange("year")}
                        />
                    </TextContent>
                </ProCheckoutButtonsContainer>
            </ProCheckoutContainer>

            <FlexLayout direction="column" gap={20} height="fill" scrollable="vertical" minWidth={280}>
                <PlanSummary
                    variant="B"
                    isTrial={isTrial}
                    billingInterval={billingInterval}
                    productDisplayName={productDisplayName}
                    prices={prices}
                    promo={promo}
                    availableReferralCredit={availableReferralCredit}
                    handleBillingIntervalChange={handleBillingIntervalChange}
                    handleVerifyPromoCode={handleVerifyPromoCode}
                />
                <PlanFeatures isAnnual={isAnnual} isTrial={isTrial} />
            </FlexLayout>
        </FlexLayout>
    );
}, { appearanceVariant: "B" }));

export const ProCheckoutContentWithContainer = ({ children }: { children: React.ReactNode }) => {
    const { isMobile, width: windowWidth } = useScreenSize();

    const useMobileLayout = isMobile || windowWidth < 916;
    const useFullScreen = isMobile || windowWidth < 652;

    return (
        <OverlayContainer useMobileLayout={useMobileLayout} useFullScreen={useFullScreen}>
            <CheckoutContentContainer>
                {children}
            </CheckoutContentContainer>

            <FlowProgressContainer>
                <ProgressInfoContainer>
                    <ProgressWrapper>
                        <ProgressBar prevProgress={66} progress={100} />
                    </ProgressWrapper>
                    <LogoWrapper>
                        <BeautifulLogo src={getStaticUrl("/images/beautifulai-logos/beautifulai-logo.svg")} alt="Beautiful Logo" />
                    </LogoWrapper>
                </ProgressInfoContainer>
            </FlowProgressContainer>
        </OverlayContainer>
    );
};

export function ProCheckoutContent(props: ProCheckoutProps) {
    const { user, firebaseUser } = useUser();

    const { workspaces, loading: workspaceLoading } = useUserWorkspaces();

    const workspace = useMemo(() => {
        if (props.workspaceId) {
            return workspaces.find(w => w.id === props.workspaceId);
        }

        const basicWorkspace = workspaces.filter(w => w.userRole === WorkspaceUserRole.OWNER && w.plan.tier === WorkspacePlanTier.BASIC)
            .sort((a, b) => a.createdAt - b.createdAt)[0];

        if (basicWorkspace) {
            return basicWorkspace;
        }

        return workspaces.filter(w => w.userRole === WorkspaceUserRole.OWNER).sort((a, b) => a.createdAt - b.createdAt)[0];
    }, [workspaceLoading]);

    useEffect(() => {
        const restoreZoom = preventZoom();
        return () => {
            restoreZoom();
        };
    }, []);

    const [billingInterval, setBillingInterval] = useState<"month" | "year">(props.defaultBillingInterval || "year");
    const [productDisplayName, setProductDisplayName] = useState<string>(props.displayName || "Pro");
    const [prices, setPrices] = useState<{ month: RecurringPlanDefinition, year: RecurringPlanDefinition } | null>(null);
    const [promo, setPromo] = useState<StripeTypes.PromotionCode | null>(null);
    const [availableReferralCredit, setAvailableReferralCredit] = useState<number | null>(null);
    const [paymentMethodCreationError, setPaymentMethodCreationError] = useState<string | null>(null);
    const [isPaymentElementComplete, setIsPaymentElementComplete] = useState<boolean>(false);
    const [billingAddress, setBillingAddress] = useState<StripeTypes.Address | null>({ country: "", postal_code: "", state: "", city: "", line1: "", line2: "" });
    const [isBillingAddressValid, setIsBillingAddressValid] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isReady, setIsReady] = useState<boolean>(false);

    useEffect(() => {
        if (!user || workspaceLoading) return;

        (async () => {
            try {
                if (!workspace) {
                    ShowMessageDialog({
                        title: t("Workspace not found"),
                        message: t("We couldn't find the workspace you're trying to upgrade. Let's take you to your presentation library."),
                        onClick: () => {
                            window.location.href = "/";
                        },
                        onClose: () => {
                            window.location.href = "/";
                        }
                    });
                    return;
                }

                if (workspace.plan.tier !== WorkspacePlanTier.BASIC) {
                    ShowMessageDialog({
                        title: t("Are you sure about this?"),
                        message: t("You are already paying for a {{planName}} plan. Let's take you to your presentation library.", { planName: workspace.plan.name }),
                        onClick: () => {
                            window.location.href = "/";
                        },
                        onClose: () => {
                            window.location.href = "/";
                        }
                    });
                    return;
                }

                const productDefinition = stripeProducts[WorkspacePlanTier.PRO];
                const prices = {
                    year: productDefinition.plans.find(plan => plan.isDefault && "interval" in plan && plan.interval === "year") as RecurringPlanDefinition,
                    month: productDefinition.plans.find(plan => plan.isDefault && "interval" in plan && plan.interval === "month") as RecurringPlanDefinition
                };

                const requestedPriceDefinitions = (props.priceIds ?? [])
                    .map(id => productDefinition.plans.find(plan => "interval" in plan && plan.planId === id))
                    .filter(Boolean) as RecurringPlanDefinition[];

                requestedPriceDefinitions.forEach(price => prices[price.interval] = price);

                setPrices(prices);
                setProductDisplayName(productDefinition.productName);
                setBillingInterval(requestedPriceDefinitions.length === 1 && !props.defaultBillingInterval
                    ? requestedPriceDefinitions[0].interval
                    : props.defaultBillingInterval || "year");

                const { totalAvailable: availableReferralCredit } = await Api.referrals.get();
                setAvailableReferralCredit(availableReferralCredit);

                const params = new URLSearchParams(window.location.search);
                const testId = params.get("ex");
                const analyticsProps = {
                    current_status: workspace.plan.type,
                    workspace_id: workspace.id,
                    price_id: prices[billingInterval].planId,
                    plan_display_name: productDisplayName,
                    test_id: testId,
                    user_id: firebaseUser.uid
                };
                trackActivity("Upgrade", "BillingIntentStart", testId, null, analyticsProps, { audit: true });

                setIsReady(true);
            } catch (err) {
                logger.error(err, "Failed to initialize ProCheckoutContent");

                ShowErrorDialog({
                    title: t("Oops, something went wrong"),
                    message: (
                        <Trans
                            i18nKey="Please contact us at <mailto>support@beautiful.ai</mailto> so we can help you directly."
                            components={{ mailto: <a href="mailto:support@beautiful.ai" /> }}
                        />
                    ),
                });
            }
        })();
    }, [user, workspaceLoading]);

    if (!isReady) {
        return (
            <FlexLayout width="fill" height="fill">
                <FetchingClickShield visible backgroundColor="white" />
            </FlexLayout>
        );
    }

    const isTrial = user.availableTrialDays[WorkspacePlanTier.PRO] > 0;
    const isAnnual = billingInterval === "year";

    const handleBillingIntervalChange = (billingInterval: "month" | "year") => {
        setBillingInterval(billingInterval);
    };

    const handlePaymentElementChange = (paymentElementChangeEvent: StripePaymentElementChangeEvent) => {
        setIsPaymentElementComplete(paymentElementChangeEvent.complete);
        setPaymentMethodCreationError(null);
    };

    const handleBillingAddressChange = ({ address, isAddressValid }: { address: Pick<StripeTypes.Address, "country" | "postal_code">, isAddressValid: boolean }) => {
        setBillingAddress({ ...address, city: "", line1: "", line2: "", state: "" });
        setIsBillingAddressValid(isAddressValid);
    };

    const handleVerifyPromoCode = async (promo: StripeTypes.PromotionCode) => {
        const { productPrice, productName, productId } = getProductDetails(prices[billingInterval].planId);

        if (promo.coupon?.applies_to?.products && !promo.coupon.applies_to.products.includes(productId)) {
            throw new Error(t("This coupon can't be applied to a {{productName}} plan", { productName }));
        }

        if (promo.restrictions?.minimum_amount && productPrice < promo.restrictions.minimum_amount) {
            throw new Error(t("This promo can't be applied to bills of less than ${{amount}}", { amount: promo.restrictions.minimum_amount / 100 }));
        }

        if (promo.restrictions?.minimum_amount && isTrial) {
            throw new Error(t("This promo can't be applied until after your free trial is over"));
        }

        if (promo.restrictions?.first_time_transaction && !isTrial) {
            throw new Error(t("This promo is only for new users"));
        }

        setPromo(promo);
    };

    const handleSubmitPayment = async (stripe: Stripe, elements: StripeElements) => {
        // Submit and create payment method BEFORE setting isSubmitting to prevent unmounting the payment element
        const { error } = await elements.submit();
        if (error) {
            setPaymentMethodCreationError(error.message);
            return;
        }

        let paymentMethod: StripeTypes.PaymentMethod | null = null;
        try {
            const { paymentMethod: createdPaymentMethod, error: paymentMethodError } = await stripe.createPaymentMethod({
                elements,
                params: {
                    billing_details: {
                        address: {
                            country: billingAddress.country,
                            postal_code: billingAddress.postal_code
                        }
                    }
                }
            });

            if (paymentMethodError) {
                throw new Error(paymentMethodError.message);
            }

            paymentMethod = createdPaymentMethod as unknown as StripeTypes.PaymentMethod;
        } catch (error) {
            logger.error(error, "Failed to create payment method");
            setPaymentMethodCreationError(error.message);
            return;
        }

        // Only show loading state after successfully creating the payment method
        setIsSubmitting(true);

        try {
            let workspaceId = workspace?.id || props.workspaceId || AppController.workspaceId;

            if (!workspaceId) {
                const firebaseUser = user.getAuthUser();
                const fallbackName = firebaseUser.email?.split("@")[0] ?? "";
                const newWorkspace = await createWorkspace({
                    name: t("{{name}}'s Workspace", { name: firebaseUser.displayName ?? fallbackName })
                });
                workspaceId = newWorkspace.id;
            }

            await upgradeWorkspacePlan({
                workspaceId,
                tier: WorkspacePlanTier.PRO,
                quantity: 1,
                priceId: prices[billingInterval].planId,
                paymentMethodId: paymentMethod.id,
                billingDetails: {
                    address: billingAddress
                },
                promotionCode: promo?.code
            });

            setPersistentWorkspaceId(workspaceId);

            if (props.onSuccess) {
                props.onSuccess();
            } else {
                if (!isPPTAddin) {
                    const continueUrl = sanitizeRelativeUrl(new URLSearchParams(window.location.search).get("continue") ?? "/");

                    // If the continue URL is the same as the current URL, we need to force a reload in order to update the user's subscription status.
                    if (continueUrl !== window.location.pathname) {
                        window.location.href = continueUrl;
                    } else {
                        window.location.reload();
                    }
                }
            }
        } catch (err) {
            logger.error(err, "Failed to submit payment");
            if (err instanceof PaymentRequiredError) {
                ShowErrorDialog({
                    title: t("There was a problem processing your payment"),
                    message: err.message
                });
            } else {
                ShowErrorDialog({
                    title: t("There was a problem upgrading your plan"),
                    message: err.message
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = props.closeDialog;

    const canSubmit = !isSubmitting && isPaymentElementComplete && isBillingAddressValid;
    const showProgressLoader = isSubmitting;

    const checkoutState: ProCheckoutState = {
        isTrial,
        isAnnual,
        isUpgradeMode: !!props.workspaceId,
        isSubmitting,
        canSubmit,
        showProgressLoader,
        paymentMethodCreationError,
        billingAddress,
        billingInterval,
        productDisplayName,
        prices,
        promo,
        availableReferralCredit,
        handleBillingIntervalChange,
        handlePaymentElementChange,
        handleBillingAddressChange,
        handleVerifyPromoCode,
        handleSubmitPayment,
        handleCancel,
    };

    return <ProCheckoutLayout {...checkoutState} stripe={props.stripe} elements={props.elements} />;
}

export default function ProCheckout(props: ProCheckoutProps) {
    const content = <ProCheckoutContent {...props} />;
    return <ProCheckoutContentWithContainer>{content}</ProCheckoutContentWithContainer>;
}

export const ProCheckoutDialog = forwardRef(
    function ProCheckoutDialog({
        style, closeDialog, hideBackdrop, ...props
    }: { style?: React.CSSProperties, closeDialog: () => void, hideBackdrop?: boolean } & ProCheckoutProps, ref: React.Ref<any>) {
        return (
            <BeautifulDialog
                preventClose={true}
                closeDialog={closeDialog}
                hideBackdrop={hideBackdrop}
                style={style}
                PaperProps={{ style: { maxHeight: "100%", minHeight: "500px", backgroundColor: "white", margin: 0 } }}
                ref={ref}
            >
                <ProCheckoutContent {...props} onSuccess={() => closeDialog()} />
            </BeautifulDialog>
        );
    }
);
